// Domains: el subdominio de siempre y los dominios propios del cliente, con instrucciones de DNS y comprobación.
import { useState, type FormEvent } from 'react';
import { CheckCircle2, Clock, Globe, MoreHorizontal, TriangleAlert } from 'lucide-react';
import { api, type Domain, type DomainCheck } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { errorText, useApp } from '../../store.ts';
import { ago } from '../../format.ts';
import { MenuButton } from '../../ui/Menu.tsx';
import type { SiteProps } from './SiteShell.tsx';

function DnsHelp({ domain, base }: { domain: Domain; base: string }) {
  const config = useApp((s) => s.config);
  const labels = domain.hostname.split('.');
  // Un dominio de dos partes (cliente.com) es la raíz: registro A. Uno más largo (www.cliente.com): CNAME.
  const apex = labels.length === 2;
  const name = apex ? '@' : labels.slice(0, -2).join('.');
  const ips = config?.serverIps.length ? config.serverIps : ['—'];
  return (
    <div className="col" style={{ gap: 8 }}>
      <span className="hint">{t('domains.dnsIntro')}</span>
      <div className="dns">
        <span className="h">{t('domains.type')}</span>
        <span className="h">{t('domains.nameCol')}</span>
        <span className="h">{t('domains.value')}</span>
        {apex ? (
          ips.map((ip) => (
            <DnsRow key={ip} type={ip.includes(':') ? 'AAAA' : 'A'} name={name} value={ip} />
          ))
        ) : (
          <DnsRow type="CNAME" name={name} value={base} />
        )}
      </div>
      <span className="hint">{t('domains.dnsWait')}</span>
    </div>
  );
}

function DnsRow({ type, name, value }: { type: string; name: string; value: string }) {
  const notify = useApp((s) => s.notify);
  return (
    <>
      <span className="mono">{type}</span>
      <span className="mono ellipsis" title={name}>
        {name}
      </span>
      <button type="button" className="btn field small mono" style={{ justifyContent: 'flex-start', minWidth: 0 }} title={t('domains.copy')} onClick={() => void navigator.clipboard.writeText(value).then(() => notify(t('domains.copied')))}>
        <span className="ellipsis">{value}</span>
      </button>
    </>
  );
}

export function Domains({ site, reload }: SiteProps) {
  const notify = useApp((s) => s.notify);
  const [host, setHost] = useState('');
  const [checks, setChecks] = useState<Record<string, DomainCheck>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.addDomain(site.id, host);
      setHost('');
      await reload();
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  const verify = async (d: Domain) => {
    setBusy(d.id);
    try {
      const r = await api.verifyDomain(d.id);
      setChecks((c) => ({ ...c, [d.id]: r.check }));
      await reload();
      notify(r.domain.status === 'active' ? t('domains.activeToast', { host: d.hostname }) : t(`domains.err.${r.check.error ?? 'unknown'}`), r.domain.status !== 'active');
    } catch (err) {
      notify(errorText(err), true);
    } finally {
      setBusy(null);
    }
  };

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await reload();
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  return (
    <>
      <section className="section">
        <h2>{t('site.domains')}</h2>
        <p className="hint">{t('domains.hint')}</p>
        <div className="list">
          <div className="list-row">
            <Globe size={14} color="var(--icon)" />
            <div className="col grow" style={{ gap: 4 }}>
              <span className="ellipsis" title={site.hosts.base}>
                {site.hosts.base}
              </span>
              <span className="hint">{site.domains.some((d) => d.primary && d.status === 'active') ? t('domains.redirectsToPrimary') : t('domains.defaultDomain')}</span>
            </div>
            {!site.domains.some((d) => d.primary && d.status === 'active') ? <span className="badge blue">{t('domains.primary')}</span> : null}
          </div>
          {site.domains.map((d) => {
            const check = checks[d.id];
            return (
              <div key={d.id} className="list-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div className="row">
                  {d.status === 'active' ? (
                    d.lastError && d.lastError !== 'dns_behind_proxy' && d.lastError !== 'check_skipped' ? (
                      <TriangleAlert size={14} color="var(--warn)" />
                    ) : (
                      <CheckCircle2 size={14} color="var(--ok)" />
                    )
                  ) : (
                    <Clock size={14} color="var(--warn)" />
                  )}
                  <div className="col grow" style={{ gap: 4 }}>
                    <span className="ellipsis" title={d.hostname}>
                      {d.hostname}
                    </span>
                    <span className="hint ellipsis">
                      {d.status === 'active'
                        ? d.lastError
                          ? t(`domains.err.${d.lastError}`)
                          : t('domains.verifiedAgo', { when: ago(d.lastCheckAt ?? d.verifiedAt) })
                        : t('domains.pending')}
                    </span>
                  </div>
                  {d.primary && d.status === 'active' ? <span className="badge blue">{t('domains.primary')}</span> : null}
                  {d.status === 'pending' ? (
                    <button type="button" className="btn small primary" disabled={busy === d.id} onClick={() => verify(d)}>
                      {busy === d.id ? t('domains.checking') : t('domains.verify')}
                    </button>
                  ) : null}
                  <MenuButton
                    align="end"
                    title={t('common.more')}
                    items={[
                      { label: t('domains.verifyAgain'), onSelect: () => void verify(d) },
                      { label: t('domains.makePrimary'), disabled: d.status !== 'active' || d.primary, onSelect: () => act(() => api.primaryDomain(d.id)) },
                      { label: t('domains.visit'), disabled: d.status !== 'active', onSelect: () => window.open(`https://${d.hostname}`, '_blank', 'noopener') },
                      { separator: true },
                      { label: t('domains.remove'), danger: true, onSelect: () => act(() => api.removeDomain(d.id)) },
                    ]}
                  >
                    <MoreHorizontal size={14} />
                  </MenuButton>
                </div>
                {d.status === 'pending' ? (
                  <div className="panel" style={{ marginTop: 10 }}>
                    <DnsHelp domain={d} base={site.hosts.base} />
                    {check ? (
                      <span className="hint">
                        {t('domains.found', { records: [...check.records.a, ...check.records.aaaa, ...check.records.cname].join(', ') || '—' })}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <form className="row" onSubmit={add}>
          <input className="input" value={host} onChange={(e) => setHost(e.target.value)} placeholder={t('domains.placeholder')} required disabled={site.archived} />
          <button className="btn" type="submit" disabled={site.archived || !host.trim()}>
            {t('domains.add')}
          </button>
        </form>
      </section>
    </>
  );
}
